import React, { useState } from "react";

/**
 * Enhanced FiltersSidebar for SubCategoryPage
 * Includes brand and rating filters in addition to standard filters
 */
const SubCategoryFiltersSidebar = ({ 
  filters, 
  onFilterChange, 
  availableBrands = [],
  parentCategory,
  subCategory
}) => {
  // Track which filter sections are expanded
  const [expandedSections, setExpandedSections] = useState({
    department: true,
    price: true,
    brand: true,
    rating: true,
    availability: true,
    discount: true,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Price ranges - Amazon style
  const priceRanges = [
    { label: 'Under KSH 1,000', min: 0, max: 1000 },
    { label: 'KSH 1,000 - 5,000', min: 1000, max: 5000 },
    { label: 'KSH 5,000 - 10,000', min: 5000, max: 10000 },
    { label: 'KSH 10,000 - 25,000', min: 10000, max: 25000 },
    { label: 'Over KSH 25,000', min: 25000, max: Infinity },
  ];

  // Rating options
  const ratingOptions = [
    { label: '4 Stars & Up', value: 4 },
    { label: '3 Stars & Up', value: 3 },
    { label: '2 Stars & Up', value: 2 },
    { label: '1 Star & Up', value: 1 },
  ];

  // Discount ranges
  const discountRanges = [
    { label: '10% off or more', value: 0.1 },
    { label: '25% off or more', value: 0.25 },
    { label: '50% off or more', value: 0.5 },
    { label: '70% off or more', value: 0.7 },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-sm p-4 sticky top-20">
      {/* Department/Breadcrumb Section */}
      {parentCategory && (
        <div className="border-b border-gray-200 pb-4 mb-4">
          <button
            onClick={() => toggleSection('department')}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-base font-bold text-gray-900">Department</h3>
            <svg
              className={`w-4 h-4 transition-transform ${expandedSections.department ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.department && (
            <div className="mt-3 space-y-2">
              <div className="text-sm text-gray-600">
                <span className="hover:text-orange-600 cursor-pointer">{parentCategory.name}</span>
                <span className="mx-1">›</span>
                <span className="font-medium text-gray-900">{subCategory?.name}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Price Filter */}
      <div className="border-b border-gray-200 pb-4 mb-4">
        <button
          onClick={() => toggleSection('price')}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-base font-bold text-gray-900">Price</h3>
          <svg
            className={`w-4 h-4 transition-transform ${expandedSections.price ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {expandedSections.price && (
          <div className="mt-3 space-y-2">
            {priceRanges.map((range, index) => (
              <label key={index} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                <input
                  type="radio"
                  name="priceRange"
                  checked={
                    filters.priceMin === range.min && filters.priceMax === range.max
                  }
                  onChange={() => {
                    onFilterChange({
                      ...filters,
                      priceMin: range.min,
                      priceMax: range.max,
                      customPriceMin: null,
                      customPriceMax: null
                    });
                  }}
                  className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">{range.label}</span>
              </label>
            ))}
            
            {/* Custom Price Range */}
            <div className="pt-2 mt-2 border-t border-gray-100">
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.customPriceMin || ''}
                  onChange={(e) => onFilterChange({
                    ...filters,
                    customPriceMin: e.target.value ? Number(e.target.value) : null,
                    priceMin: null,
                    priceMax: null
                  })}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.customPriceMax || ''}
                  onChange={(e) => onFilterChange({
                    ...filters,
                    customPriceMax: e.target.value ? Number(e.target.value) : null,
                    priceMin: null,
                    priceMax: null
                  })}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <button
                onClick={() => {
                  if (filters.customPriceMin || filters.customPriceMax) {
                    onFilterChange({
                      ...filters,
                      priceMin: filters.customPriceMin || 0,
                      priceMax: filters.customPriceMax || Infinity
                    });
                  }
                }}
                className="w-full mt-2 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 rounded transition-colors"
              >
                Go
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Brand Filter */}
      {availableBrands && availableBrands.length > 0 && (
        <div className="border-b border-gray-200 pb-4 mb-4">
          <button
            onClick={() => toggleSection('brand')}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-base font-bold text-gray-900">Brand</h3>
            <svg
              className={`w-4 h-4 transition-transform ${expandedSections.brand ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.brand && (
            <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
              {availableBrands.map((brand) => (
                <label key={brand} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={filters.selectedBrands?.includes(brand) || false}
                    onChange={(e) => {
                      const newBrands = e.target.checked
                        ? [...(filters.selectedBrands || []), brand]
                        : (filters.selectedBrands || []).filter(b => b !== brand);
                      onFilterChange({ ...filters, selectedBrands: newBrands });
                    }}
                    className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{brand}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rating Filter */}
      <div className="border-b border-gray-200 pb-4 mb-4">
        <button
          onClick={() => toggleSection('rating')}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-base font-bold text-gray-900">Customer Reviews</h3>
          <svg
            className={`w-4 h-4 transition-transform ${expandedSections.rating ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {expandedSections.rating && (
          <div className="mt-3 space-y-2">
            {ratingOptions.map((option) => (
              <label key={option.value} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                <input
                  type="radio"
                  name="rating"
                  checked={filters.minRating === option.value}
                  onChange={() => onFilterChange({
                    ...filters,
                    minRating: option.value
                  })}
                  className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500"
                />
                <span className="ml-2 flex items-center text-sm text-gray-700">
                  <span className="flex text-orange-400 mr-1">
                    {'★'.repeat(option.value)}{'☆'.repeat(5 - option.value)}
                  </span>
                  & Up
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Availability Filter */}
      <div className="border-b border-gray-200 pb-4 mb-4">
        <button
          onClick={() => toggleSection('availability')}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-base font-bold text-gray-900">Availability</h3>
          <svg
            className={`w-4 h-4 transition-transform ${expandedSections.availability ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {expandedSections.availability && (
          <div className="mt-3 space-y-2">
            <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
              <input
                type="checkbox"
                checked={filters.inStockOnly || false}
                onChange={(e) => onFilterChange({
                  ...filters,
                  inStockOnly: e.target.checked
                })}
                className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-700">In Stock Only</span>
            </label>
          </div>
        )}
      </div>

      {/* Discount Filter */}
      <div className="pb-4">
        <button
          onClick={() => toggleSection('discount')}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-base font-bold text-gray-900">Deals & Discounts</h3>
          <svg
            className={`w-4 h-4 transition-transform ${expandedSections.discount ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {expandedSections.discount && (
          <div className="mt-3 space-y-2">
            {discountRanges.map((range, index) => (
              <label key={index} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                <input
                  type="radio"
                  name="discount"
                  checked={filters.minDiscount === range.value}
                  onChange={() => onFilterChange({
                    ...filters,
                    minDiscount: range.value
                  })}
                  className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">{range.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Clear All Filters */}
      {(filters.selectedBrands?.length > 0 || 
        filters.priceMin || 
        filters.priceMax || 
        filters.inStockOnly || 
        filters.minDiscount ||
        filters.minRating) && (
        <button
          onClick={() => onFilterChange({
            selectedBrands: [],
            priceMin: null,
            priceMax: null,
            customPriceMin: null,
            customPriceMax: null,
            inStockOnly: false,
            minDiscount: null,
            minRating: null
          })}
          className="w-full mt-4 px-4 py-2 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors border border-orange-300"
        >
          Clear all filters
        </button>
      )}
    </div>
  );
};

export default SubCategoryFiltersSidebar;
